import { getClient } from "./client";
import { translateError, VixioError } from "./errors";

/**
 * Arquivo guardado no Storage, como devolvem as RPCs de download (docs.file_link, portal.document_file,
 * certificates.pdf_link): a RPC confere a permissão no banco e o Storage confere de novo pela política do bucket
 * ao assinar a URL. O link assinado vale poucos minutos e nunca é guardado.
 */
export interface FileLink {
  bucket: string;
  storage_path: string;
  file_name: string;
  variant?: string;
  sha256?: string;
}

/** Gera a URL assinada (2 minutos) e abre o download no navegador. */
export async function downloadFileLink(link: FileLink, expiresInSeconds = 120): Promise<string> {
  let res;
  try {
    res = await getClient().storage.from(link.bucket).createSignedUrl(link.storage_path, expiresInSeconds, { download: link.file_name });
  } catch (e) {
    throw translateError(e);
  }
  if (res.error || !res.data?.signedUrl) {
    throw new VixioError("Não foi possível liberar o arquivo agora. Tente de novo em instantes.", "unavailable", null, res.error?.message ?? null);
  }
  const url = res.data.signedUrl;
  if (typeof document !== "undefined") {
    const a = document.createElement("a");
    a.href = url;
    a.download = link.file_name;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  return url;
}

/**
 * Envia um arquivo ao Storage num caminho já reservado por uma RPC (ex.: portal.begin_upload): a política do bucket
 * só aceita o caminho reservado pelo próprio usuário, sem sobrescrever. Erros do Storage viram mensagem em pt-BR.
 */
export async function uploadToStorage(bucket: string, path: string, file: Blob, contentType: string): Promise<void> {
  let res;
  try {
    // O tipo vai na parte do formulário: usa o tipo conferido (o navegador às vezes informa outro ou nenhum).
    const body = file.type === contentType ? file : new Blob([file], { type: contentType });
    res = await getClient().storage.from(bucket).upload(path, body, { contentType, upsert: false, cacheControl: "3600" });
  } catch (e) {
    throw translateError(e);
  }
  if (res.error) {
    const raw = res.error.message ?? "";
    const msg = /size|large|413/i.test(raw)
      ? "O arquivo é maior que o permitido."
      : /mime|type/i.test(raw)
        ? "Tipo de arquivo não aceito."
        : /row-level|policy|unauthor|403/i.test(raw)
          ? "Envio não autorizado. Atualize a página e tente de novo."
          : "Não foi possível enviar o arquivo agora. Tente de novo em instantes.";
    throw new VixioError(msg, "unavailable", null, raw);
  }
}
